// @ts-nocheck
import memoize from 'lodash/memoize';
import { BottomCandlesType } from './bloomberg';
import {
  OptionAccumulationType,
  OptionOrigin,
} from 'gen/stream-api/requests/types.gen';

import { IChartingLibraryWidget } from 'charting_library';
import { Theme } from '@mui/material/styles';
import { HiroAlertCategory, HiroAlertCategories } from './alerts';

export interface TrendingData {
  currentDaySignal: number;
  instrument: string;
  signal_date?: string;
  hiro_timeseries?: number[];
  price: number;
}

// As returned from `v3/trending` endpoint
export interface TrendingElement {
  sym: number;
  trend: number;
  trend_abs: number;
  instrument: string;
}

export interface AverageData {
  high: number;
  low: number;
  average: number;
}

export interface HiroCumulativeData {
  '1 day': AverageData;
  '5 day': AverageData;
  '30 day': AverageData;
}

export interface HiroTimeRange {
  from: number;
  to: number;
}

export interface RawHiroOverview {
  companyName: string;
  currentDayPrice?: string;
  currentDaySignal?: string;
  symbol: string;
  high1?: number;
  high5?: number;
  high20?: number;
  low1?: number;
  low5?: number;
  low20?: number;
  lastClose?: number;
  day?: string;
  sector?: string;
  live?: boolean;
}

export interface LowHigh {
  low: number;
  current?: number;
  high: number;
}

export interface HiroOverview {
  instrument: string;
  signal: number;
  companyName: string;
  '1 day': LowHigh;
  '5 day': LowHigh;
  '30 day': LowHigh;
  price: number;
  lastClose: number;
  open: number;
  priceChange: number;
  sector: string;
  live?: boolean;
}

export enum HiroLense {
  All = 'all',
  NextExp = 'nextExp',
  Retail = 'retail',
}

export interface HiroStatistics {
  symbol: string;
  name: string;
  exchange: string;
  mic_code: string;
  currency: string;
  datetime: string;
  timestamp: number;
  open: string;
  high: string;
  low: string;
  volume: string;
  previous_close: string;
  change: string;
  percent_change: string;
  average_volume: string;
  rolling_1d_change: string;
  rolling_7d_change: string;
  rolling_period_change: string;
  is_market_open: boolean;
  fifty_two_week: {
    low: string;
    high: string;
    low_change: string;
    high_change: string;
    low_change_percent: string;
    high_change_percent: string;
    range: string;
  };
  extended_change: string;
  extended_percent_change: string;
  extended_price: string;
  extended_timestamp: string;
}

export enum Index {
  SPY = 'SPY',
  QQQ = 'QQQ',
}

export interface IndicesStatistics {
  [Index.SPY]: HiroStatistics;
  [Index.QQQ]: HiroStatistics;
}

export interface RawSpotgammaLevels {
  Symbol: string;
  activity_factor: number;
  atmgc: number;
  atmgp: number;
  call_gnot_list_absg: string;
  call_strikes_list_absg: string;
  callsum: number;
  cg_list: string;
  cv: number;
  date: string;
  dpi: number;
  keyd: number;
  keyg: number;
  largeCoi: number;
  largePoi: number;
  max_exp_d_date: string;
  max_exp_g_date: string;
  mf_list: string;
  minfs: number;
  next_exp_d: number;
  next_exp_g: number;
  pg_list: string;
  position_factor: number;
  put_gnot_list_absg: string;
  put_strikes_list_absg: string;
  putctrl: number;
  putsum: number;
  pv: number;
  pws: number;
  smf_list: string;
  strike_list: string;
  sym: string;
  total_volume: number;
  totaldelta: number;
  totalgamma: number;
  trade_date: string;
  upx: number;
  maxfs: number;
  cws: number;
  sig_high: number;
  sig_low: number;
}

export interface RawHiroAndPrice {
  utc_time: number;
  instrument: string;
  mid_signal: number;
  option_type: string;
  signal: number;
  stock_price: number;
}

export interface HiroAndPrice {
  utc_time: number;
  mid_signal: number;
  signal: number;
  stock_price: number;
  type: string;
}

export interface GroupedRawHiro {
  [ALL: string]: (HiroAndPrice | RawHiroAndPrice)[];
  P: (HiroAndPrice | RawHiroAndPrice)[];
  C: (HiroAndPrice | RawHiroAndPrice)[];
}

export interface ValueToMarker {
  '1': { label: string; seconds: number };
  '2': { label: string; seconds: number };
  '3': { label: string; seconds: number };
  '4': { label: string; seconds: number };
  '5': { label: string; seconds: number };
  '6': { label: string; seconds: number };
  '7': { label: string; seconds: number };
  '8': { label: string; seconds: number };
}

export type ValToMarkerKey = keyof ValueToMarker;

export interface SparkData {
  [x: string]: string[];
}

export interface RawHiroMap {
  all: [HiroAndPrice];
  nextExp: [HiroAndPrice];
  retail: [HiroAndPrice];
}

export interface RawHiroTrend {
  all: [HiroAndPrice];
}

export interface RawTrending {
  [instrument: string]: RawHiroTrend;
}

export type HiroTimeseries = {
  all: { TOT: RawHiroAndPrice[]; P: RawHiroAndPrice[]; C: RawHiroAndPrice[] };
  nextExp: {
    TOT: RawHiroAndPrice[];
    P: RawHiroAndPrice[];
    C: RawHiroAndPrice[];
  };
  retail: {
    TOT: RawHiroAndPrice[];
    P: RawHiroAndPrice[];
    C: RawHiroAndPrice[];
  };
};

export const HIRO_LINE_TYPES = ['Line', 'Candle', 'OHLC'];
export type HiroLineType = (typeof HIRO_LINE_TYPES)[number];

export type HiroChartSettings = {
  showExtendedHours: boolean;
  showKeyLevels: boolean;
  showChartTooltip: boolean;
  showAlerts: boolean;
  sumWindow: number;
  selectedLenses: {
    all: number;
    nextExp: number;
    retail: number;
  };
  optionType: string;
  showSpotgammaLevels: boolean;
  bottomCandlesType: BottomCandlesType;
  candleDurationSecs: number;
  hiroLineType: HiroLineType;
  priceLineType: HiroLineType;
};

export enum HiroUpdatingType {
  STREAMING,
  POLLING,
}

export type PriceLineData = {
  value: number | undefined;
  color: string;
};

export const PriceLineKeys = [
  'Last Closing',
  'Hedge Wall',
  'Key Gamma Strike',
  'Key Delta Strike',
  'Call Wall',
  'Put Wall',
  'SG Implied 1d Move High',
  'SG Implied 1d Move Low',
];
export type PriceLineKey = (typeof PriceLineKeys)[number];

export type PriceData = {
  sym: string;
  date: string;
  price: number;
};

export type SigHighLowData = {
  sig_high: number;
  sig_low: number;
};

export const OptionAccumulationTypes: OptionAccumulationType[] = [
  'Calls',
  'Puts',
  'Total',
];

export const OptionOrigins: OptionOrigin[] = ['All', 'NextExpiry', 'Retail'];

export interface HiroChartDatafeedIntfc {
  chartingLibraryWidget?: IChartingLibraryWidget;
  theme?: Theme;
  fetchAPIWithLog: (
    endpoint: string,
    auxiliaryOptions?: FetchAuxOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
}

export const HiroIndicatorOptionOrigins: OptionOrigin[] = ['All', 'NextExpiry'];

export enum OptionOriginEnum {
  All = 'All',
  NextExpiry = 'NextExpiry',
  Retail = 'Retail',
}

export enum OptionAccumulationTypeEnum {
  Calls = 'Calls',
  Puts = 'Puts',
  Total = 'Total',
}

export interface DeltaLineParams {
  optionAccumulationType: OptionAccumulationType;
  optionOrigin: OptionOrigin;
  isFiltered: boolean;
}

export type SymbolLineParams = {
  symbol: string;
} & (
  | { kind: 'hiro'; deltaLineParams: DeltaLineParams }
  | { kind: 'oldhiro'; deltaLineParams: DeltaLineParams }
  | { kind: 'price' }
  | { kind: 'key level'; keyLevel: PriceLineKey }
  | { kind: 'alert'; alertCategory: HiroAlertCategory }
);

export type SymbolLineKind = SymbolLineParams['kind'];

export const nameFromSymbolLineParams = memoize(
  (params: SymbolLineParams): string => {
    switch (params.kind) {
      case 'hiro':
      case 'oldhiro': {
        const d = params.deltaLineParams;
        return `${params.symbol}  ${params.kind === 'oldhiro' ? 'OldHiro  ' : ''}${d.optionAccumulationType}  ${d.optionOrigin}${d.isFiltered ? '' : '  Unfiltered'}`;
      }
      case 'price':
        return `${params.symbol}`;
      case 'key level':
        return `${params.symbol}  Key Level  ${params.keyLevel}`;
      case 'alert':
        return `${params.symbol}  Alert  ${params.alertCategory}`;
    }
  },
);

export const genericNameFromSymbolLineParams = memoize(
  (params: SymbolLineParams): string => {
    switch (params.kind) {
      case 'hiro':
      case 'oldhiro': {
        const d = params.deltaLineParams;
        return `${params.kind === 'oldhiro' ? 'OldHiro  ' : ''}${d.optionAccumulationType}  ${d.optionOrigin}${d.isFiltered ? '' : '  Unfiltered'}`;
      }
      case 'price':
        return `Price`;
      case 'key level':
        return `${params.symbol}  Key Level  ${params.keyLevel}`;
      case 'alert':
        return `${params.symbol}  Alert  ${params.alertCategory}`;
    }
  },
);

export const genericSymbolineParamsEqual = (
  a: SymbolLineParams,
  b: SymbolLineParams,
): boolean =>
  a != null &&
  b != null &&
  ((a.kind == 'price' && b.kind === 'price') ||
    (a.kind == 'hiro' &&
      b.kind === 'hiro' &&
      a.deltaLineParams.optionAccumulationType ===
        b.deltaLineParams.optionAccumulationType &&
      a.deltaLineParams.optionOrigin === b.deltaLineParams.optionOrigin));

// prettier-ignore
const DecodeSymbolLineParamsRegexStr = (
  '^' +
  '(?<symbol>.+?)' +
  '(?<postSymText>' +
    '(?:' +
      '  ' +
      '(?:' +
        (
          'Key Level' +
          '  ' +
          '(?<keyLevel>' +
            PriceLineKeys.join('|') +
          ')'
        ) + '|' +
        (
          'Alert' +
          '  ' +
          '(?<alert>' +
            HiroAlertCategories.join('|') +
          ')'
        ) + '|' +
        (
          '(?<oldHiro>(?:OldHiro  )?)' +
          '(?<optionAccumulationType>' +
            OptionAccumulationTypes.join('|') +
          ')' +
          '  ' +
          '(?<optionOrigin>' +
            HiroIndicatorOptionOrigins.join('|') +
          ')' +
          '(?:' +
            '  ' +
            '(?<unfiltered>unfiltered)' +
          ')?'
        ) +
      ')' +
    ')?' +
  ')' +
  '$'

);

const DecodeSymbolLineParamsRegex = new RegExp(
  DecodeSymbolLineParamsRegexStr,
  'i', // charting_library changes the string casing some times
);

export const decodeSymbolLineParams = memoize(
  (name: string): Readonly<SymbolLineParams> | undefined => {
    const match = DecodeSymbolLineParamsRegex.exec(name);
    if (match == null) {
      console.error(`Unable to decode symbol line params from '${name}'`);
      return undefined;
    }
    const {
      groups: {
        symbol,
        postSymText,
        keyLevel,
        alert,
        oldHiro,
        optionAccumulationType,
        optionOrigin,
        unfiltered,
      } = {},
    } = match;

    if (!postSymText?.length) {
      return {
        symbol,
        kind: 'price',
      };
    }
    if (keyLevel) {
      return {
        symbol,
        kind: 'key level',
        keyLevel: PriceLineKeys.find(
          (k) => k.toLowerCase() == keyLevel.toLowerCase(),
        ) as PriceLineKey,
      };
    }

    if (alert) {
      return {
        symbol,
        kind: 'alert',
        alertCategory: HiroAlertCategories.find(
          (k) => k.toLowerCase() == alert.toLowerCase(),
        ) as HiroAlertCategory,
      };
    }

    return {
      symbol,
      kind: oldHiro ? 'oldhiro' : 'hiro',
      deltaLineParams: {
        optionAccumulationType: OptionAccumulationTypes.find(
          (k) => k.toLowerCase() === optionAccumulationType.toLowerCase(),
        )!,
        optionOrigin: HiroIndicatorOptionOrigins.find(
          (k) => k.toLowerCase() === optionOrigin.toLowerCase(),
        )!,
        isFiltered: !unfiltered,
      },
    };
  },
);

export const OPTION_ORIGIN_TO_PALETTE = new Map<OptionOriginEnum, HiroLense>([
  [OptionOriginEnum.All, HiroLense.All],
  [OptionOriginEnum.NextExpiry, HiroLense.NextExp],
  [OptionOriginEnum.Retail, HiroLense.Retail],
]);

export const OPTION_ACCUM_TO_PALETTE = new Map<
  OptionAccumulationTypeEnum,
  string
>([
  [OptionAccumulationTypeEnum.Calls, 'call'],
  [OptionAccumulationTypeEnum.Puts, 'put'],
  [OptionAccumulationTypeEnum.Total, 'total'],
]);

export interface DividendRecord {
  symbol: string;
  date: string;
  label: string | null;
  adj_dividend: number;
  dividend: number | null;
  record_date: string | null;
  payment_date: string | null;
  declaration_date: string | null;
  is_forecast: boolean;
}

export interface HiroIndicator {
  seriesName?: string;
  symbolLineParams?: SymbolLineParams;
  indicatorName?: string;
  constructorInputs?: (string | null)[];
  deleting?: boolean;
  colors: string[];
}
